import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtDate, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Plus, Trash2, Save, AlertTriangle } from "lucide-react";
import { StatusBadge } from "@/pages/employee/WeeklySummary";

// -----------------------------------------------------------------------------
// ISO ↔ <input type="datetime-local"> conversion helpers.
//
// The <input> expects "YYYY-MM-DDTHH:MM" in the LOCAL wall-clock of the user's
// browser. Backend punches are stored as full ISO with UTC offset, so we round-
// trip through the browser's local timezone here.
// -----------------------------------------------------------------------------
function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToIso(v) {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "";
  return d.toISOString();
}

export default function EmployeeDayEdit() {
  const { id: subId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [punches, setPunches] = useState([]);
  const [lunchStart, setLunchStart] = useState("");
  const [lunchEnd, setLunchEnd] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/daily/submissions/${subId}`);
        setDetail(data);
        const te = data?.time_entry || {};
        const seed = (te.punches && te.punches.length
          ? te.punches
          : (te.clock_in ? [{ id: "legacy", clock_in: te.clock_in, clock_out: te.clock_out || "" }] : [])
        ).map((p) => ({
          id: p.id,
          clock_in: isoToLocalInput(p.clock_in),
          clock_out: isoToLocalInput(p.clock_out),
        }));
        setPunches(seed.length ? seed : [{ id: `tmp-${Math.random().toString(36).slice(2, 10)}`, clock_in: "", clock_out: "" }]);
        const le = data?.lunch_entry || {};
        setLunchStart(isoToLocalInput(le.lunch_start));
        setLunchEnd(isoToLocalInput(le.lunch_end));
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || e.message);
      }
    })();
    // Only refetch when the route param changes. All other referenced
    // symbols (api, isoToLocalInput, formatApiError) are stable imports.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subId]);

  const dayLockedByPayroll = detail?.submission?.status === "locked";

  // Client-side ID for freshly-added punches so React reconciliation stays
  // stable across removes (using array index as key here caused focus to
  // hop between fields when a middle row was removed).
  const nextTempId = () => `tmp-${Math.random().toString(36).slice(2, 10)}`;

  const addPunch = () => setPunches((p) => [...p, { id: nextTempId(), clock_in: "", clock_out: "" }]);
  const removePunch = (i) => setPunches((p) => p.filter((_, idx) => idx !== i));
  const updatePunch = (i, field, val) =>
    setPunches((p) => p.map((row, idx) => (idx === i ? { ...row, [field]: val } : row)));

  const totalHours = useMemo(() => {
    let mins = 0;
    for (const p of punches) {
      if (!p.clock_in || !p.clock_out) continue;
      const a = new Date(p.clock_in);
      const b = new Date(p.clock_out);
      if (isNaN(a.getTime()) || isNaN(b.getTime())) continue;
      mins += Math.max(0, (b - a) / 60000);
    }
    if (lunchStart && lunchEnd) {
      const a = new Date(lunchStart);
      const b = new Date(lunchEnd);
      if (!isNaN(a.getTime()) && !isNaN(b.getTime())) mins -= Math.max(0, (b - a) / 60000);
    }
    return Math.max(0, mins / 60);
  }, [punches, lunchStart, lunchEnd]);

  const reasonInvalid = reason.trim().length < 10;
  const punchesInvalid = punches.some((p) => !p.clock_in) || punches.length === 0;

  const submit = async () => {
    if (reasonInvalid) {
      toast.error("Please explain the edit (10+ characters).");
      return;
    }
    if (punchesInvalid) {
      toast.error("Every punch needs a start time.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        date: detail.submission.date,
        reason: reason.trim(),
        punches: punches.map((p) => ({
          id: p.id && p.id !== "legacy" ? p.id : undefined,
          clock_in: localInputToIso(p.clock_in),
          clock_out: p.clock_out ? localInputToIso(p.clock_out) : "",
        })),
      };
      // Only include lunch fields if the user actually filled them in on this
      // form. Sending empty strings CLEARS lunch on the backend, which is what
      // we want if the tech deletes it, but null values are skipped.
      if (lunchStart) payload.lunch_start = localInputToIso(lunchStart);
      else if (detail?.lunch_entry?.lunch_start) payload.lunch_start = ""; // clear
      if (lunchEnd) payload.lunch_end = localInputToIso(lunchEnd);
      else if (detail?.lunch_entry?.lunch_end) payload.lunch_end = "";

      await api.post("/time/request-edit", payload);
      toast.success("Edit submitted — awaiting admin approval");
      navigate("/week");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (error) {
    return (
      <div className="py-16 text-center" data-testid="day-edit-error">
        <p className="text-red-700 font-semibold">{error}</p>
        <Link to="/week" className="text-sky-700 underline mt-3 inline-block">← Back to Week</Link>
      </div>
    );
  }
  if (!detail) return <div className="py-20 text-center text-slate-500">Loading…</div>;

  const sub = detail.submission;

  return (
    <div className="space-y-5" data-testid="day-edit-page">
      <header className="flex items-start justify-between gap-3">
        <div>
          <Link to="/week" className="text-xs text-sky-700 font-semibold inline-flex items-center gap-1" data-testid="back-to-week-link">
            <ArrowLeft className="h-3 w-3" /> Back to Week
          </Link>
          <h1 className="text-2xl font-extrabold font-display tracking-tighter text-slate-900 mt-1">
            Request Time Edit
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {fmtDate(sub.date)} · <StatusBadge status={sub.status} />
          </p>
        </div>
      </header>

      {dayLockedByPayroll && (
        <Card className="rounded-xl border-red-200 bg-red-50" data-testid="day-locked-payroll-banner">
          <CardContent className="p-3 text-sm text-red-800 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <p className="font-bold">Payroll is already locked for this day.</p>
              <p className="text-xs">Ask your admin to reopen the payroll period before requesting an edit.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {!dayLockedByPayroll && (
        <Card className="rounded-xl border-sky-200 bg-sky-50" data-testid="day-edit-notice">
          <CardContent className="p-3 text-sm text-sky-900">
            Change your punches or lunch below and add a short reason. Your day will move to
            <b> Pending Approval</b> — your admin reviews the change before it counts toward pay.
          </CardContent>
        </Card>
      )}

      {/* Show the admin's reopen note (or last rejection note) so the tech
          knows exactly what needs fixing this time. */}
      {(sub.reopened_reason || (sub.status === "rejected" && sub.admin_notes)) && (
        <Card
          className={`rounded-xl ${sub.reopened_reason ? "border-sky-300 bg-sky-100" : "border-red-300 bg-red-50"}`}
          data-testid="day-edit-admin-note"
        >
          <CardContent className="p-3 text-sm flex items-start gap-2">
            <span className="text-base leading-none mt-0.5">{sub.reopened_reason ? "🔄" : "🚫"}</span>
            <div className="min-w-0">
              <p className={`font-bold ${sub.reopened_reason ? "text-sky-900" : "text-red-900"}`}>
                {sub.reopened_reason
                  ? `${sub.reopened_by_name || "Admin"} reopened this day — here's what to fix:`
                  : "Rejected by admin — here's what to fix:"}
              </p>
              <p className={`mt-0.5 whitespace-pre-wrap break-words ${sub.reopened_reason ? "text-sky-800" : "text-red-800"}`}>
                {sub.reopened_reason || sub.admin_notes}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* -------------------------------------------------------- Punches */}
      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold uppercase tracking-widest text-slate-700">Punches</h2>
            <Button
              variant="outline"
              size="sm"
              onClick={addPunch}
              disabled={dayLockedByPayroll}
              data-testid="add-punch-btn"
            >
              <Plus className="h-3.5 w-3.5 mr-1" /> Add Punch
            </Button>
          </div>
          <div className="space-y-3">
            {punches.map((p, i) => (
              <div key={p.id || `idx-${i}`} className="rounded-xl border border-slate-200 p-3" data-testid={`punch-row-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-slate-500">Punch #{i + 1}</span>
                  {punches.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 h-7"
                      onClick={() => removePunch(i)}
                      disabled={dayLockedByPayroll}
                      data-testid={`remove-punch-${i}-btn`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor={`ci-${i}`} className="text-xs text-slate-500">Clock In</Label>
                    <Input
                      id={`ci-${i}`}
                      type="datetime-local"
                      value={p.clock_in}
                      onChange={(e) => updatePunch(i, "clock_in", e.target.value)}
                      disabled={dayLockedByPayroll}
                      data-testid={`punch-in-${i}-input`}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`co-${i}`} className="text-xs text-slate-500">Clock Out</Label>
                    <Input
                      id={`co-${i}`}
                      type="datetime-local"
                      value={p.clock_out}
                      onChange={(e) => updatePunch(i, "clock_out", e.target.value)}
                      disabled={dayLockedByPayroll}
                      data-testid={`punch-out-${i}-input`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* -------------------------------------------------------- Lunch */}
      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-4 space-y-3">
          <h2 className="text-sm font-bold uppercase tracking-widest text-slate-700">Lunch (optional)</h2>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ls" className="text-xs text-slate-500">Lunch Start</Label>
              <Input
                id="ls"
                type="datetime-local"
                value={lunchStart}
                onChange={(e) => setLunchStart(e.target.value)}
                disabled={dayLockedByPayroll}
                data-testid="lunch-start-input"
              />
            </div>
            <div>
              <Label htmlFor="le" className="text-xs text-slate-500">Lunch End</Label>
              <Input
                id="le"
                type="datetime-local"
                value={lunchEnd}
                onChange={(e) => setLunchEnd(e.target.value)}
                disabled={dayLockedByPayroll}
                data-testid="lunch-end-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* -------------------------------------------------------- Totals preview */}
      <Card className="rounded-2xl border-slate-200 bg-slate-50">
        <CardContent className="p-4 flex items-center justify-between">
          <span className="text-xs uppercase tracking-widest text-slate-500 font-bold">New Net Hours</span>
          <span className="text-2xl font-extrabold font-display text-slate-900" data-testid="edit-net-hours">
            {totalHours.toFixed(2)}h
          </span>
        </CardContent>
      </Card>

      {/* -------------------------------------------------------- Reason */}
      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-4 space-y-2">
          <Label htmlFor="reason" className="text-sm font-bold text-slate-700">
            Why are you requesting this edit? <span className="text-red-600">*</span>
          </Label>
          <Textarea
            id="reason"
            placeholder="Example: Forgot to clock out for lunch at Henderson site — was on lunch 12:15–12:45."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={500}
            disabled={dayLockedByPayroll}
            data-testid="edit-reason-input"
          />
          <div className="flex justify-between text-xs">
            <span className={reasonInvalid ? "text-red-600" : "text-slate-500"}>
              {reasonInvalid ? "Minimum 10 characters." : "Looks good."}
            </span>
            <span className="text-slate-400">{reason.length}/500</span>
          </div>
        </CardContent>
      </Card>

      {/* -------------------------------------------------------- Submit */}
      <div className="sticky bottom-4 z-10">
        <Button
          className="w-full h-14 rounded-2xl bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-base font-bold shadow-lg"
          disabled={saving || dayLockedByPayroll || reasonInvalid || punchesInvalid}
          onClick={submit}
          data-testid="submit-edit-request-btn"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? "Submitting…" : "Submit for Admin Review"}
        </Button>
      </div>
    </div>
  );
}
